import { useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { CompanyContext } from "@/context/CompanyContext";

export const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export const BrandMark = ({ name, logoUrl, className, testid = "brand-mark" }) =>
  logoUrl ? (
    <div className={cn("flex shrink-0 items-center justify-center overflow-hidden", className)} data-testid={testid}>
      <img src={logoUrl} alt={`${name} logo`} className="h-full w-full object-contain" data-testid={`${testid}-img`} />
    </div>
  ) : (
    <div className={cn("flex shrink-0 items-center justify-center rounded-md bg-teal-600 text-[0.7em] font-semibold text-white", className)} data-testid={testid}>
      {initials(name)}
    </div>
  );

/** Any company's logo (light variant by default), cache-busted by logo_version; `url` serves viewer-link logos directly. */
export const CompanyLogo = ({ company, url, variant = "light", className, testid }) => {
  const [blob, setBlob] = useState(null);
  useEffect(() => {
    if (url || !company.has_logo) return undefined;
    let href;
    api.get("/company/logo", { params: { variant, v: company.logo_version }, responseType: "blob", headers: { "X-Company-Id": company.id } })
      .then((r) => { href = URL.createObjectURL(r.data); setBlob(href); }).catch(() => {});
    return () => href && URL.revokeObjectURL(href);
  }, [company.id, company.has_logo, company.logo_version, url, variant]);
  return <BrandMark name={company.name} logoUrl={url || (company.has_logo ? blob : null)} className={className} testid={testid} />;
};

/** Current company's logo + name for light page headers; renders nothing outside a company context. */
export const CompanyBadge = ({ className, testid = "company-badge" }) => {
  const ctx = useContext(CompanyContext);
  if (!ctx?.company) return null;
  const { company, lightLogoUrl } = ctx;
  return (
    <div className={cn("inline-flex max-w-full items-center gap-2 rounded-full bg-white py-1 pl-1.5 pr-3 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200", className)} data-testid={testid}>
      <BrandMark name={company.name} logoUrl={lightLogoUrl} className={lightLogoUrl ? "h-6 w-12" : "h-6 w-6 rounded-full text-[10px]"} testid={`${testid}-logo`} />
      <span className="truncate" data-testid={`${testid}-name`}>{company.name}</span>
    </div>
  );
};
